import React, { useState, useEffect } from 'react';
import { Tabs, Input, Select, Button, List, Alert, Spin, Typography, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useConstants } from '../context/ConstantsContext';
import { updateSiteContacts, updateSiteContract, updateSiteQBCustomerMapping } from '../api';
import EquipmentRatesTab from './SiteEditEquipment';
import QBCustomerSelect from './QBCustomerSelect';

// Module-level constant for empty QB options fallback
const EMPTY_QB_OPTIONS = { hierarchicalOptions: [], optionMap: new Map() };

const SiteEdit = ({ site, messageApi, qbOptionsCA, qbOptionsUS, qbCustomersLoading, onSiteUpdate }) => {
  const constants = useConstants();

  const [siteContactData, setSiteContactData] = useState({
    notification_email: site.notification_email || '',
    cc_emails: site.cc_emails || [],
  });

  const [siteContractData, setSiteContractData] = useState({
    salting_contract_type: site.salting_contract_type,
    snow_contract_type: site.snow_contract_type,
    salting_amount: site.salting_amount || 500,
    saltings_included: site.saltings_included || 20,
    saltings_performed: site.saltings_performed || 0,
    per_accum_client_rate: site.per_accum_client_rate || 75,
    per_accum_subcontractor_rate: site.per_accum_subcontractor_rate || 50,
    client_per_push_rate: site.client_per_push_rate || 0,
    subcontractor_per_push_rate: site.subcontractor_per_push_rate || 0,
  });

  // QB Customer mapping state
  const [selectedQBCustomer, setSelectedQBCustomer] = useState(site.precoro_qb_customer_option_id);
  const [qbMappingLoading, setQbMappingLoading] = useState(false);

  // Determine which QB options to use based on site country
  const qbOptions = site.country === 'Canada' ? qbOptionsCA : site.country === 'USA' ? qbOptionsUS : EMPTY_QB_OPTIONS;

  // Update state when site prop changes
  useEffect(() => {
    setSiteContactData({
      notification_email: site.notification_email || '',
      cc_emails: site.cc_emails || [],
    });

    setSiteContractData({
      salting_contract_type: site.salting_contract_type,
      snow_contract_type: site.snow_contract_type,
      salting_amount: site.salting_amount || 500,
      saltings_included: site.saltings_included || 20,
      saltings_performed: site.saltings_performed || 0,
      per_accum_client_rate: site.per_accum_client_rate || 75,
      per_accum_subcontractor_rate: site.per_accum_subcontractor_rate || 50,
      client_per_push_rate: site.client_per_push_rate || 0,
      subcontractor_per_push_rate: site.subcontractor_per_push_rate || 0,
    });

    setSelectedQBCustomer(site.precoro_qb_customer_option_id ?? null);
  }, [site]);

  const detailSections = [
    {
      title: 'Basic Information',
      items: [
        { label: 'Site Name', value: site.site_name },
        { label: 'Site Reference', value: site.site_reference || 'N/A' },
        { label: 'Client Name', value: site.client_name },
        { label: 'Client Reference', value: site.client_reference || 'N/A' },
        {
          label: 'QuickBooks Customer',
          value: site.precoro_qb_customer ? (
            <div style={{ whiteSpace: 'pre-line' }}>
              {site.precoro_qb_customer.parent_chain?.length > 0 && (
                <div style={{ color: '#8c8c8c', fontSize: '12px', marginBottom: '2px' }}>
                  {site.precoro_qb_customer.parent_chain.join(' › ')}
                </div>
              )}
              <div style={{ fontWeight: 600 }}>
                {site.precoro_qb_customer.name}
              </div>
            </div>
          ) : (
            'Not mapped'
          )
        },
        {
          label: 'Site Area',
          value: (() => {
            const areaM2 = site.area_m2;
            const isInvalid = !areaM2 || areaM2 === 0;
            const sqFt = areaM2 ? (areaM2 * 10.7639).toFixed(0) : null;
            
            return (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                <div>
                  {isInvalid ? (
                    <span style={{ color: '#8c8c8c' }}>Not set</span>
                  ) : (
                    <>
                      {areaM2.toFixed(2)} m²
                      <span style={{ color: '#8c8c8c', marginLeft: '8px' }}>
                        ({sqFt} sq ft)
                      </span>
                    </>
                  )}
                </div>
                {isInvalid && (
                  <Tooltip title="1 acre (4,047 m²) will be used as default for calculations">
                    <div style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '4px',
                      color: '#d48806',
                      fontSize: '12px'
                    }}>
                      <InfoCircleOutlined />
                      <span>Default (1 acre) will be used</span>
                    </div>
                  </Tooltip>
                )}
              </div>
            );
          })()
        },
      ],
      group: 1
    },
    {
      title: 'Location',
      items: [
        { label: 'Street Address', value: site.street_address },
        { label: 'City', value: site.city },
        { label: 'Province/State', value: site.province_state },
        { label: 'Postal/Zip Code', value: site.postal_zip_code },
        { label: 'Country', value: site.country },
        { label: 'Coordinates', value: `${site.latitude?.toFixed(6)}, ${site.longitude?.toFixed(6)}` },
      ],
      group: 1
    },
    {
      title: 'Contact Information',
      items: [
        { label: 'Notification Email', value: site.notification_email || 'Using client email' },
        {
          label: 'CC Emails',
          value: site.cc_emails?.length ? (
            <div style={{ whiteSpace: 'pre-line' }}>
              {site.cc_emails.join('\n')}
            </div>
          ) : (
            'None'
          ),
        },
      ],
      group: 1
    },
    {
      title: 'Salting Contract',
      items: [
        { label: 'Contract Type', value: site.salting_contract_type },
        { label: 'Contract Amount', value: site.salting_amount ? `$${site.salting_amount?.toFixed(2)}` : 'N/A' },
        { label: 'Saltings Included', value: site.saltings_included },
        { label: 'Saltings Performed', value: site.saltings_performed },
      ],
      group: 2
    },
    {
      title: 'Snow Contract',
      items: [
        { label: 'Contract Type', value: site.snow_contract_type },
        { 
          label: `Per ${site.country === constants.country.canada ? 'CM' : 'Inch'} (Client)`, 
          value: site.per_accum_client_rate ? `$${site.per_accum_client_rate?.toFixed(2)}` : 'N/A'
        },
        { 
          label: `Per ${site.country === constants.country.canada ? 'CM' : 'Inch'} (Subcontractor)`, 
          value: site.per_accum_subcontractor_rate ? `$${site.per_accum_subcontractor_rate?.toFixed(2)}` : 'N/A'
        },
        { 
          label: 'Per-Push Rate (Client)', 
          value: site.client_per_push_rate ? `$${site.client_per_push_rate?.toFixed(2)}` : 'N/A'
        },
        { 
          label: 'Per-Push Rate (Subcontractor)', 
          value: site.subcontractor_per_push_rate ? `$${site.subcontractor_per_push_rate?.toFixed(2)}` : 'N/A'
        },
      ],
      group: 2
    },
    {
      title: 'Weather & Routes',
      items: [
        { 
          label: 'Weather Station', 
          value: site.weather_station_name || 'Not assigned'
        },
        { 
          label: 'Override Weather Station', 
          value: site.override_weather_station_name || 'None'
        },
        {
          label: 'Routes',
          value: site.routes_details?.length ? (
            <div style={{ whiteSpace: 'pre-line' }}>
              {site.routes_details.map(r => r.route_name).join('\n')}
            </div>
          ) : (
            'Not assigned to any routes'
          ),
        },
      ],
      group: 2
    },
  ];

  const group1Sections = detailSections.filter(s => s.group === 1);
  const group2Sections = detailSections.filter(s => s.group === 2);

  /* --------------------------------------------
   * Handlers for siteContactData
   * -------------------------------------------- */
  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setSiteContactData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const addCCEmailRow = () => {
    setSiteContactData((prev) => ({
      ...prev,
      cc_emails: [...prev.cc_emails, ''],
    }));
  };

  const handleCCEmailChange = (index, value) => {
    const updatedCC = [...siteContactData.cc_emails];
    updatedCC[index] = value;
    setSiteContactData((prev) => ({
      ...prev,
      cc_emails: updatedCC,
    }));
  };

  const deleteCCEmailRow = (index) => {
    const updatedCC = [...siteContactData.cc_emails];
    updatedCC.splice(index, 1);
    setSiteContactData((prev) => ({
      ...prev,
      cc_emails: updatedCC,
    }));
  };

  /* --------------------------------------------
   * Handlers for siteContractData
   * -------------------------------------------- */
  const handleContractSelect = (fieldName, value) => {
    setSiteContractData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleContractInputChange = (e) => {
    const { name, value } = e.target;
    setSiteContractData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* --------------------------------------------
   * Submit handlers
   * -------------------------------------------- */
  const handleSubmitSiteContacts = async (e) => {
    e.preventDefault();
    try {
      const updatedSite = await updateSiteContacts(site.site_id, siteContactData);
      messageApi.success('Site contacts updated successfully.');
      if (onSiteUpdate) {
        onSiteUpdate(updatedSite);
      }
    } catch (error) {
      console.error('Error updating site contacts', error);
      messageApi.error('Failed to update site contacts.');
    }
  };

  const handleSubmitSiteContract = async (e) => {
    e.preventDefault();
    try {
      const updatedSite = await updateSiteContract(site.site_id, siteContractData);
      messageApi.success('Site contract updated successfully.');
      if (onSiteUpdate) {
        onSiteUpdate(updatedSite);
      }
    } catch (error) {
      console.error('Error updating site contract', error);
      messageApi.error('Failed to update site contract.');
    }
  };

  /* --------------------------------------------
   * Handlers for QB Customer mapping
   * -------------------------------------------- */
  const handleQBCustomerChange = async (value) => {
    // Update state immediately for better UX
    setSelectedQBCustomer(value);
    setQbMappingLoading(true);
    try {
      const updatedSite = await updateSiteQBCustomerMapping(site.site_id, value);
      messageApi.success('QuickBooks customer mapping updated successfully.');
      if (onSiteUpdate) {
        onSiteUpdate(updatedSite);
      }
    } catch (error) {
      console.error('Error updating QB customer mapping:', error);
      messageApi.error('Failed to update QuickBooks customer mapping.');
      // Revert state on error
      setSelectedQBCustomer(site.precoro_qb_customer_option_id);
    } finally {
      setQbMappingLoading(false);
    }
  };

  const handleClearQBCustomer = async () => {
    setQbMappingLoading(true);
    try {
      const updatedSite = await updateSiteQBCustomerMapping(site.site_id, null);
      setSelectedQBCustomer(null);
      messageApi.success('QuickBooks customer mapping cleared.');
      if (onSiteUpdate) {
        onSiteUpdate(updatedSite);
      }
    } catch (error) {
      console.error('Error clearing QB customer mapping:', error);
      messageApi.error('Failed to clear QuickBooks customer mapping.');
    } finally {
      setQbMappingLoading(false);
    }
  };

  /* --------------------------------------------
   * Render CC Email Rows
   * -------------------------------------------- */
  const renderCCEmailRows = () =>
    siteContactData.cc_emails.map((email, index) => (
      <div
        key={index}
        style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}
      >
        <Input
          placeholder="CC Email"
          value={email}
          onChange={(e) => handleCCEmailChange(index, e.target.value)}
          style={{ flexGrow: 1 }}
        />
        <DeleteOutlined
          onClick={() => deleteCCEmailRow(index)}
          style={{ marginLeft: '10px', fontSize: '18px', cursor: 'pointer' }}
        />
      </div>
    ));

  const tabItems = [
    {
      key: 'details',
      label: 'Details',
      children: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* First row: Basic Info, Location, Contacts */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '48px'
          }}>
            {group1Sections.map((section, idx) => (
              <div key={idx}>
                <Typography.Title level={5} style={{ 
                  marginBottom: '8px', 
                  paddingBottom: '4px', 
                  borderBottom: '1px solid #e8e8e8',
                  fontSize: '14px',
                  fontWeight: 600
                }}>
                  {section.title}
                </Typography.Title>
                <List
                  dataSource={section.items}
                  size="small"
                  split={false}
                  renderItem={(item) => (
                    <List.Item style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      padding: '4px 0'
                    }}>
                      <div style={{ 
                        fontSize: '13px',
                        color: '#595959',
                        minWidth: '120px'
                      }}>
                        {item.label}
                      </div>
                      <div style={{ 
                        textAlign: 'right', 
                        flex: 1,
                        fontSize: '13px',
                        color: '#262626'
                      }}>
                        {item.value || 'N/A'}
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            ))}
          </div>

          {/* Second row: Contracts, Weather & Routes */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '48px'
          }}>
            {group2Sections.map((section, idx) => (
              <div key={idx}>
                <Typography.Title level={5} style={{ 
                  marginBottom: '8px', 
                  paddingBottom: '4px', 
                  borderBottom: '1px solid #e8e8e8',
                  fontSize: '14px',
                  fontWeight: 600
                }}>
                  {section.title}
                </Typography.Title>
                <List
                  dataSource={section.items}
                  size="small"
                  split={false}
                  renderItem={(item) => (
                    <List.Item style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      padding: '4px 0'
                    }}>
                      <div style={{ 
                        fontSize: '13px',
                        color: '#595959',
                        minWidth: '120px'
                      }}>
                        {item.label}
                      </div>
                      <div style={{ 
                        textAlign: 'right', 
                        flex: 1,
                        fontSize: '13px',
                        color: '#262626'
                      }}>
                        {item.value || 'N/A'}
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      key: 'contact',
      label: 'Edit Site Contacts',
      children: (
        <form onSubmit={handleSubmitSiteContacts}>
          <label style={labelStyle}>
            Notification Email (overrides client contact for notification emails)
          </label>
          <Input
            name="notification_email"
            value={siteContactData.notification_email}
            onChange={handleContactChange}
            placeholder="Notification Email (optional)"
            style={{ marginBottom: '10px' }}
          />

          <label style={{ ...labelStyle, display: 'block' }}>CC Emails</label>
          {renderCCEmailRows()}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={addCCEmailRow}
          >
            Add CC Email
          </Button>

          <Button
            block
            style={{ marginTop: '20px', marginBottom: '20px' }}
            onClick={handleSubmitSiteContacts}
          >
            Update Site Contact
          </Button>
        </form>
      )
    },
    {
      key: 'contract',
      label: 'Edit Site Contract',
      children: (
        <form onSubmit={handleSubmitSiteContract}>
          <label style={labelStyle}>Salting Contract Type</label>
          <Select
            name="salting_contract_type"
            value={
              siteContractData.salting_contract_type === 'Event Assigned'
                ? undefined
                : siteContractData.salting_contract_type
            }
            onChange={(val) => handleContractSelect('salting_contract_type', val)}
            options={[
              {
                value: constants.contract_type.per_visit,
                label: constants.contract_type.per_visit,
              },
              {
                value: constants.contract_type.flat_rate,
                label: constants.contract_type.flat_rate,
              },
              {
                value: constants.contract_type.all_inclusive,
                label: constants.contract_type.all_inclusive,
              },
            ]}
            style={{ width: '100%', marginBottom: '10px' }}
            placeholder="Salting Contract Type"
          />

          <label style={labelStyle}>Snow Contract Type</label>
          <Select
            name="snow_contract_type"
            value={
              siteContractData.snow_contract_type === 'Event Assigned'
                ? undefined
                : siteContractData.snow_contract_type
            }
            onChange={(val) => handleContractSelect('snow_contract_type', val)}
            options={[
              {
                value: constants.contract_type.hourly,
                label: constants.contract_type.hourly,
              },
              {
                value: constants.contract_type.accum,
                label: constants.contract_type.accum,
              },
              {
                value: constants.contract_type.per_push,
                label: constants.contract_type.per_push,
              },
              {
                value: constants.contract_type.all_inclusive,
                label: constants.contract_type.all_inclusive,
              },
            ]}
            style={{ width: '100%', marginBottom: '10px' }}
            placeholder="Snow Contract Type"
          />

          <label style={labelStyle}>Salting Amount</label>
          <Input
            name="salting_amount"
            value={siteContractData.salting_amount}
            onChange={handleContractInputChange}
            type="number"
            placeholder="Salting Amount"
            style={{ marginBottom: '10px' }}
          />

          <label style={labelStyle}>Saltings Included</label>
          <Input
            name="saltings_included"
            value={siteContractData.saltings_included}
            onChange={handleContractInputChange}
            type="number"
            placeholder="Saltings Included"
            style={{ marginBottom: '10px' }}
          />

          <label style={labelStyle}>Saltings Performed</label>
          <Input
            name="saltings_performed"
            value={siteContractData.saltings_performed}
            onChange={handleContractInputChange}
            type="number"
            placeholder="Saltings Performed"
            style={{ marginBottom: '10px' }}
          />

          <label style={labelStyle}>
            Per {site.country === constants.country.canada ? 'CM' : 'Inch'} (Client)
          </label>
          <Input
            name="per_accum_client_rate"
            value={siteContractData.per_accum_client_rate}
            onChange={handleContractInputChange}
            type="number"
            placeholder={`Per ${
              site.country === constants.country.canada ? 'CM' : 'Inch'
            } (Client)`}
            style={{ marginBottom: '10px' }}
          />

          <label style={labelStyle}>
            Per {site.country === constants.country.canada ? 'CM' : 'Inch'} (Subcontractor)
          </label>
          <Input
            name="per_accum_subcontractor_rate"
            value={siteContractData.per_accum_subcontractor_rate}
            onChange={handleContractInputChange}
            type="number"
            placeholder={`Per ${
              site.country === constants.country.canada ? 'CM' : 'Inch'
            } (Subcontractor)`}
            style={{ marginBottom: '10px' }}
          />

          <label style={labelStyle}>Per-Push Rate (Client)</label>
          <Input
            name="client_per_push_rate"
            value={siteContractData.client_per_push_rate}
            onChange={handleContractInputChange}
            type="number"
            step="0.01"
            placeholder="Per-Push Rate (Client)"
            style={{ marginBottom: '10px' }}
          />

          <label style={labelStyle}>Per-Push Rate (Subcontractor)</label>
          <Input
            name="subcontractor_per_push_rate"
            value={siteContractData.subcontractor_per_push_rate}
            onChange={handleContractInputChange}
            type="number"
            step="0.01"
            placeholder="Per-Push Rate (Subcontractor)"
            style={{ marginBottom: '10px' }}
          />

          <Button
            block
            style={{ marginTop: '20px', marginBottom: '20px' }}
            onClick={handleSubmitSiteContract}
          >
            Update Site Contract
          </Button>
        </form>
      )
    },
    {
      key: 'equipment-rates',
      label: 'Equipment Rates',
      children: (
        <EquipmentRatesTab
          site={site}
          messageApi={messageApi}
          constants={constants}
        />
      )
    },
    {
      key: 'qb-customer',
      label: 'QuickBooks Customer',
      children: (
        <div style={{ padding: '10px 0' }}>
          {site.country === 'Unknown' ? (
            <Alert
              message="Cannot map QuickBooks customer"
              description="This site has an unknown country. Please update the site country in Yeti or IOS first."
              type="warning"
              showIcon
            />
          ) : (
            <>
              <label style={labelStyle}>QuickBooks Customer (Precoro)</label>
              <div style={{ marginBottom: '10px' }}>
                <small style={{ color: '#888' }}>
                  This mapping determines which QuickBooks customer is used when posting POs to Precoro.
                  Only customers matching the site's country ({site.country}) are shown.
                </small>
              </div>
              
              {qbCustomersLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin tip="Loading QuickBooks customers..." />
                </div>
              ) : (
                <>
                  <QBCustomerSelect
                    hierarchicalOptions={qbOptions.hierarchicalOptions}
                    optionMap={qbOptions.optionMap}
                    value={selectedQBCustomer}
                    onChange={handleQBCustomerChange}
                    placeholder="Select QuickBooks customer"
                    style={{ marginBottom: '10px' }}
                    loading={qbMappingLoading}
                    disabled={qbMappingLoading}
                    allowClear={false}
                  />
                  
                  {selectedQBCustomer && (
                    <Button
                      icon={<CloseCircleOutlined />}
                      onClick={handleClearQBCustomer}
                      loading={qbMappingLoading}
                      style={{ marginTop: '10px' }}
                    >
                      Clear Mapping
                    </Button>
                  )}

                  {!selectedQBCustomer && (
                    <Alert
                      message="No QuickBooks customer mapped"
                      description="This site is not currently mapped to a QuickBooks customer in Precoro. When POs are posted, the customer field will not be auto-populated."
                      type="info"
                      showIcon
                      style={{ marginTop: '10px' }}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      )
    }
  ];

  return <Tabs defaultActiveKey="details" items={tabItems} />;
};

const labelStyle = {
  width: '100%',
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: '500',
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  marginLeft: '5px',
  color: '#4a4a4a',
  marginBottom: '5px',
};

export default SiteEdit;